import React, { useState } from "react";
import { countries } from "countries-list";
import MetaData from "../../components/layout/MetaData";
import { useDispatch, useSelector } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { FaCreditCard, FaPaypal, FaShieldAlt } from "react-icons/fa";
import {
  savePaymentMethod,
  saveShippingInfo,
} from "../../redux/features/cartSlice";
import { caluclateOrderCost } from "../../helpers/caluclateOrderCost";
import "./CheckoutPage.css";

/**
 * 根据已有收货信息生成可编辑的账单地址初始值。
 * @param {Object} shippingInfo 收货信息
 * @param {Object} user 当前用户
 * @returns {Object} 账单地址表单值
 */
const createBillingAddress = (shippingInfo = {}, user = {}) => ({
  country: shippingInfo.country || "United States",
  firstName: shippingInfo.firstName || user.name || "",
  lastName: shippingInfo.lastName || "",
  companyName: shippingInfo.companyName || "",
  mailStop: shippingInfo.mailStop || "",
  addressLine1: shippingInfo.addressLine1 || shippingInfo.address || "",
  addressLine2: shippingInfo.addressLine2 || "",
  city: shippingInfo.city || "",
  stateProvince: shippingInfo.stateProvince || "",
  postalCode: shippingInfo.postalCode || shippingInfo.zipCode || "",
});

/**
 * DigiKey 风格支付方式页：选择支付渠道并进入确认订单页。
 */
const PaymentMethod = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { cartItems, shippingInfo, paymentMethod } = useSelector(
    (state) => state.cart
  );
  const { user } = useSelector((state) => state.auth);
  const [method, setMethod] = useState(paymentMethod || "Paypal");
  const [billingAddress, setBillingAddress] = useState(() =>
    createBillingAddress(shippingInfo, user)
  );
  const countriesList = Object.values(countries);
  const { itemsPrice, shippingPrice, totalPrice } =
    caluclateOrderCost(cartItems);
  const itemCount = cartItems.reduce(
    (total, item) => total + Number(item?.quantity || 0),
    0
  );

  /**
   * 更新指定账单地址字段。
   * @param {React.ChangeEvent<HTMLInputElement|HTMLSelectElement>} event 字段变更事件
   */
  const handleBillingFieldChange = (event) => {
    const { name, value } = event.target;
    setBillingAddress((currentAddress) => ({
      ...currentAddress,
      [name]: value,
    }));
  };

  /**
   * 保存支付方式并进入订单确认页。
   * @param {React.FormEvent<HTMLFormElement>} event 表单提交事件
   */
  const submitHandler = (event) => {
    event.preventDefault();
    dispatch(
      saveShippingInfo({
        ...shippingInfo,
        ...billingAddress,
        address: billingAddress.addressLine1,
        zipCode: billingAddress.postalCode,
      })
    );
    dispatch(savePaymentMethod(method));
    navigate("/confirm_order");
  };

  return (
    <>
      <MetaData title="Payment Method" />
      <main className="checkout-reference-page">
        <div className="checkout-reference-container">
          <nav className="checkout-breadcrumb" aria-label="Breadcrumb">
            <Link to="/cart">
              <i className="fas fa-shopping-cart" aria-hidden="true"></i>
              Shopping Cart
            </Link>
            <span>/</span>
            <strong>Payment</strong>
          </nav>

          <div className="checkout-review-bars" aria-label="Checkout progress">
            <div>
              <span>Shipping Address</span>
              <Link to="/shipping">Edit</Link>
            </div>
            <div>
              <span>Delivery Details</span>
              <Link to="/shipping">Edit</Link>
            </div>
          </div>

          <form className="checkout-reference-layout" onSubmit={submitHandler}>
            <section className="checkout-main-column">
              <h1>Payment Method</h1>
              <div className="checkout-panel payment-method-panel">
                <div className="payment-choice-list" role="radiogroup" aria-label="Payment method">
                  <label className={`payment-choice ${method === "Paypal" ? "is-selected" : ""}`}>
                    <input
                      type="radio"
                      name="payment_mode"
                      value="Paypal"
                      checked={method === "Paypal"}
                      onChange={(event) => setMethod(event.target.value)}
                    />
                    <FaPaypal aria-hidden="true" />
                    <span>Pay with PayPal</span>
                  </label>
                  <label className={`payment-choice ${method === "WireTransfer" ? "is-selected" : ""}`}>
                    <input
                      type="radio"
                      name="payment_mode"
                      value="WireTransfer"
                      checked={method === "WireTransfer"}
                      onChange={(event) => setMethod(event.target.value)}
                    />
                    <FaCreditCard aria-hidden="true" />
                    <span>Wire Transfer</span>
                  </label>
                </div>

                <label className="billing-same-address">
                  <input type="checkbox" checked readOnly />
                  <span>My billing address is the same as my shipping address</span>
                </label>

                <div className="checkout-section-heading">
                  <h2>Billing Address</h2>
                </div>
                <div className="checkout-address-form">
                  <label className="checkout-address-field checkout-address-country">
                    <span>Country/Region *</span>
                    <select name="country" value={billingAddress.country} onChange={handleBillingFieldChange} required>
                      {countriesList.map((countryItem) => (
                        <option key={countryItem.name} value={countryItem.name}>{countryItem.name}</option>
                      ))}
                    </select>
                  </label>

                  <label className="checkout-address-field">
                    <span>First Name *</span>
                    <input name="firstName" value={billingAddress.firstName} onChange={handleBillingFieldChange} required />
                  </label>
                  <label className="checkout-address-field">
                    <span>Last Name *</span>
                    <input name="lastName" value={billingAddress.lastName} onChange={handleBillingFieldChange} required />
                  </label>
                  <label className="checkout-address-field checkout-address-wide">
                    <span>Company Name</span>
                    <input name="companyName" value={billingAddress.companyName} onChange={handleBillingFieldChange} />
                  </label>
                  <label className="checkout-address-field checkout-address-wide">
                    <span>Mail Stop</span>
                    <input name="mailStop" value={billingAddress.mailStop} onChange={handleBillingFieldChange} />
                  </label>
                  <label className="checkout-address-field checkout-address-wide">
                    <span>Address Line 1 *</span>
                    <input name="addressLine1" value={billingAddress.addressLine1} onChange={handleBillingFieldChange} required />
                  </label>
                  <label className="checkout-address-field checkout-address-wide">
                    <span>Address Line 2</span>
                    <input name="addressLine2" value={billingAddress.addressLine2} onChange={handleBillingFieldChange} />
                  </label>
                  <label className="checkout-address-field">
                    <span>City *</span>
                    <input name="city" value={billingAddress.city} onChange={handleBillingFieldChange} required />
                  </label>
                  <label className="checkout-address-field">
                    <span>State/Province *</span>
                    <input name="stateProvince" value={billingAddress.stateProvince} onChange={handleBillingFieldChange} required />
                  </label>
                  <label className="checkout-address-field checkout-address-postal">
                    <span>Postal Code *</span>
                    <input name="postalCode" value={billingAddress.postalCode} onChange={handleBillingFieldChange} required />
                  </label>
                </div>

                <button type="submit" className="checkout-primary-button">
                  Continue
                </button>
              </div>
            </section>

            <aside className="checkout-sidebar">
              <section className="checkout-summary-card">
                <h2>Summary</h2>
                <dl>
                  <div>
                    <dt>Items in cart:</dt>
                    <dd>{itemCount}</dd>
                  </div>
                  <div>
                    <dt>Subtotal:</dt>
                    <dd>${itemsPrice}</dd>
                  </div>
                  <div>
                    <dt>Shipping:</dt>
                    <dd>${Number(shippingPrice).toFixed(2)}</dd>
                  </div>
                  <div className="checkout-summary-total">
                    <dt>Total:</dt>
                    <dd>${totalPrice}</dd>
                  </div>
                </dl>
              </section>
              <div className="checkout-security-card">
                <FaShieldAlt aria-hidden="true" />
                <span>Your data will be encrypted and transmitted securely.</span>
              </div>
            </aside>
          </form>
        </div>
      </main>
    </>
  );
};

export default PaymentMethod;
